import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import AgreementFormWithLivePreview from '../components/agreements/AgreementFormWithLivePreview';

/**
 * AgreementBuilder Page
 *
 * Allows brokers, agents, and self-managed landlords to create/edit agreements
 * with live preview and auto-calculations
 *
 * Accessible only to:
 * - broker_admin
 * - agent
 * - landlord (self_managed = true)
 *
 * NOT accessible to:
 * - tenant
 * - landlord (self_managed = false, sign-only)
 */
export default function AgreementBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  // Check if user is authorized to create agreements
  const canCreateAgreement = () => {
    if (!user) return false;

    // Brokers and agents can create
    if (['broker_admin', 'agent'].includes(user.role)) {
      return true;
    }

    // Only self-managed landlords can create
    if (user.role === 'landlord' && user.self_managed) {
      return true;
    }

    return false;
  };

  // If user not authorized, show access denied message
  if (!canCreateAgreement()) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="card-base p-8 border border-red-200 bg-red-50 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto" />
            <h1 className="text-2xl font-bold text-red-900">Access Denied</h1>
            <p className="text-red-800">
              Only brokers, agents, and self-managed landlords can create agreements.
            </p>
            <button
              onClick={() => navigate('/app')}
              className="mt-6 w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get broker ID for agreement creation
  const brokerId = user.broker_admin_id || user.id;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold font-display text-gray-900">
            {t('page_create_agreement')}
          </h1>
          <p className="text-gray-600 mt-2">
            {t('page_fill_details')}
          </p>
        </div>

        <AgreementFormWithLivePreview
          brokerId={brokerId}
          onAgreementCreated={(agreementId) => {
            // After agreement creation, optionally redirect or show success
            console.log('Agreement created:', agreementId);
          }}
          onStepComplete={(stepNum) => {
            console.log('Step completed:', stepNum);
          }}
        />
      </div>
    </div>
  );
}
